import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { css } from '@emotion/css';
import { useSprintWebSocket, useBlocksSubscription, useNavigationResult } from '../context/SprintWebSocketContext';
import BlockRenderer from '../components/BlockRenderer';
import { useMultiPageLessonStore, useIsMultiPageLesson } from '../store/multiPageLessonStore';
import { MultiPageLesson } from './MultiPageLesson';
// Import auto-generated types
import { Block } from '../../types/sprintLessonTypes';

const LessonPage: React.FC = () => {
  const { lessonId, journeyId } = useParams<{ lessonId: string; journeyId?: string }>();
  const navigate = useNavigate();
  const { sendJsonMessage, isConnected } = useSprintWebSocket();
  
  // Component state
  const [lessonBlocks, setLessonBlocks] = useState<Block[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Multi-page lesson store
  const { lessonSchema } = useMultiPageLessonStore();
  const isMultiPageLesson = useIsMultiPageLesson();

  // Debug logging
  // console.log('LessonPage debug:', {
  //   lessonSchema,
  //   isMultiPageLesson,
  //   blocksLength: lessonBlocks.length,
  //   lessonSchemaPropertyNames: lessonSchema?.properties ? Object.keys(lessonSchema.properties) : 'no properties',
  //   firstFewBlocks: lessonBlocks.slice(0, 3).map(b => ({ index_key: b.index_key, block_type: b.block_type }))
  // });
  
  // Additional debug for multi-page detection
  if (lessonSchema?.properties) {
    const propertyNames = Object.keys(lessonSchema.properties);
    // console.log('Schema properties:', propertyNames);
    // console.log('Has page fields?', propertyNames.some(name => name.startsWith('page_')));
  }

  // Subscribe to blocks updates
  useBlocksSubscription((blocks) => {
    setLessonBlocks(prev => {
      // Merge new blocks with existing blocks, updating duplicates
      const updatedBlocks = [...prev];
      blocks.forEach((newBlock: Block) => {
        const existingIndex = updatedBlocks.findIndex(block => block.index_key === newBlock.index_key);
        if (existingIndex >= 0) {
          updatedBlocks[existingIndex] = newBlock;
        } else {
          updatedBlocks.push(newBlock);
        }
      });
      return updatedBlocks;
    });
    setIsLoading(false);
  });

  // Subscribe to navigation results
  useNavigationResult((result) => {
    setIsLoading(false);
  });
  
  // Redirect to sprint page if lesson or journey ID is missing
  useEffect(() => {
    if (!lessonId || !journeyId) {
      navigate('/sharp-sprint');
      return;
    }
  }, [lessonId, journeyId, navigate]);

  const containerStyle = css`
    max-width: 900px;
    margin: 0 auto;
    padding: 1rem;
  `;

  const titleStyle = css`
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 1.5rem;
  `;

  const breadcrumbStyle = css`
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
  `;

  const breadcrumbLinkStyle = css`
    color: #3b82f6;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  `;

  const breadcrumbSeparatorStyle = css`
    margin: 0 0.5rem;
    color: #9ca3af;
  `;

  const loadingContainerStyle = css`
    display: flex;
    justify-content: center;
    margin-top: 2rem;
    margin-bottom: 2rem;
  `;

  const pulsatingGradientStyle = css`
    font-size: 1.25rem;
    font-weight: 500;
    text-align: center;
    margin: 3rem auto;
    background-image: linear-gradient(45deg, #4f46e5, #10b981, #3b82f6, #8b5cf6);
    background-size: 300% 300%;
    color: transparent;
    background-clip: text;
    -webkit-background-clip: text;
    animation: gradientPulse 3s ease infinite;

    @keyframes gradientPulse {
      0% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }
  `;

  // Handle MCQ selection
  const handleMcqSelection = (blockId: string, selectedOption: string) => {
    // Only send if we have a lessonId and journeyId from URL params
    if (lessonId && journeyId) {
      sendJsonMessage({
        type: "mcq_selection",
        block_id: blockId,
        selected_option: selectedOption,
        journey_id: journeyId,
        lesson_id: lessonId
      });
    }
  };

  // Get lesson information from the blocks if available
  const lessonTitle = lessonBlocks.length > 0 
    ? lessonBlocks.find(block => block.block_type === 'markdown_content')?.content?.raw_markdown?.split('\n')[0].replace(/^#+\s*/, '') 
    : 'Lesson';

  return (
    <div className={containerStyle}>
      <div className={breadcrumbStyle}>
        <span 
          className={breadcrumbLinkStyle}
          onClick={() => navigate('/sharp-sprint')}
        >
          Journeys
        </span>
        <span className={breadcrumbSeparatorStyle}>/</span>
        <span>{lessonTitle}</span>
      </div>

      {/* <h1 className={titleStyle}>{lessonTitle}</h1> */}

      {!isConnected && (
        <div className={loadingContainerStyle}>
          <p>WebSocket disconnected. Attempting to reconnect...</p>
        </div>
      )}
      
      {isLoading && (
        <div className={loadingContainerStyle}>
          <p>Loading lesson content...</p>
        </div>
      )}

      {!isLoading && lessonBlocks.length > 0 && (
        <div>
          {isMultiPageLesson ? (
            <MultiPageLesson 
              lessonSchema={lessonSchema} 
            />
          ) : (
            lessonBlocks
              .sort((a, b) => parseInt(a.index_key) - parseInt(b.index_key))
              .map(block => (
                <BlockRenderer 
                  key={block.index_key} 
                  block={block} 
                  onMcqSelection={handleMcqSelection}
                />
              ))
          )}
        </div>
      )}

      {!isLoading && lessonBlocks.length === 0 && (
        <p className={pulsatingGradientStyle}>Building your lesson...</p>
      )}
    </div>
  );
};

export default LessonPage;
